import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Heart, Sparkles, Package, ShoppingBag, ArrowRight } from "lucide-react";

export default function Login() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) navigate("/dashboard");
  }, [user, loading, navigate]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-zinc-50">
      {/* Left — brand / value */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-zinc-900 text-white relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-orange-500/10 blur-3xl" />
        <div className="flex items-center gap-2.5 relative">
          <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center">
            <Heart className="w-5 h-5 text-white" fill="white" />
          </div>
          <span className="font-display font-bold text-lg tracking-tight">Heartline</span>
        </div>

        <div className="relative">
          <h1 className="font-display text-4xl font-extrabold leading-[1.1] tracking-tight max-w-md">
            Turn an emotion into a ready-to-sell product — on every channel.
          </h1>
          <p className="mt-5 text-zinc-300 text-base max-w-md leading-relaxed">
            Pick a trigger — a pet memorial, a first anniversary, a hometown — and Heartline
            generates conversion-scored concepts, full SEO listings, and photorealistic mockups
            in one click. The whole POD pipeline, not just a form.
          </p>
          <div className="mt-8 space-y-3 max-w-md">
            {[
              [Sparkles, "AI concept engine with impulse-buy scoring"],
              [Package, "Complete listing packages — title, tags, copy, mockup"],
              [ShoppingBag, "Post-purchase personalization workflow"],
            ].map(([Icon, t], i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-zinc-200">
                <div className="w-8 h-8 rounded-md bg-white/10 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                {t}
              </div>
            ))}
          </div>
        </div>
        <div className="text-xs text-zinc-500 relative">Internal operations · T-shirts · Mugs · Phone cases</div>
      </div>

      {/* Right — login */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" fill="white" />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">Heartline</span>
          </div>
          <p className="overline text-blue-600">Internal Access</p>
          <h2 className="font-display text-2xl font-bold tracking-tight mt-2">Sign in to the engine</h2>
          <p className="text-sm text-zinc-500 mt-2 mb-8">
            Use your team Google account to access the product pipeline.
          </p>
          <button
            onClick={handleLogin}
            data-testid="google-login-btn"
            className="w-full h-12 rounded-md border border-zinc-200 bg-white hover:bg-zinc-50 transition-colors flex items-center justify-center gap-3 font-medium text-sm shadow-sm"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
            Continue with Google
            <ArrowRight className="w-4 h-4 text-zinc-400" />
          </button>
          <p className="text-xs text-zinc-400 mt-6 text-center">
            Restricted to authorized team members only.
          </p>
        </div>
      </div>
    </div>
  );
}
